import { defineComponent, PropType, reactive, watch } from '@vue/composition-api';
import { ConnectionStatus } from '@/views/connections/connections-types';
import { IntensoService, enableIntenso, disableIntenso } from '@/functions/services-functions';
import { inputDialogue, confirmDialogue, generalError, alertDialogue } from '@/functions/system-modal-functions';
import { i18n } from '@/utilities/vue-i18n';
import { ModalIconType } from '../system-modals/system-modals';

const validateTemplateFilter = (input: string) => !!input && input.trim().length > 0 && input.trim().length <= 256;

interface SettingsType {
  connection: IntensoService;
  loading: boolean;
  company: number;
}

export default defineComponent({
  props: {
    connection: { type: Object as PropType<IntensoService>, required: true },
    companyId: { type: Number, required: false },
    allowDelete: { type: Boolean, required: false, default: false },
    loading: { type: Boolean, required: false, default: false },
  },
  setup(props: any, context: any) {
    const settings = reactive<SettingsType>({
      connection: { ...props.connection },
      loading: props.loading,
      company: props.companyId ?? +context.root.$route.params.company,
    });

  watch(() => props.connection, (v: IntensoService) => settings.connection = { ...v }, { deep: true });
  watch(() => props.loading, (v: boolean) => settings.loading = v ?? settings.loading);
  watch(() => props.companyId, (v: number) => settings.company = v ?? settings.company);

    const enable = async () => {
      if (settings.loading) return;
      settings.loading = true;
      const dialogue = await inputDialogue({
        title: 'Intenso',
        message: i18n.t('components.connection-intenso.add-intenso-template-filter'),
        validation: validateTemplateFilter,
      });

      if (dialogue.confirmed) {
        const service = await enableIntenso(settings.company, dialogue.input.trim());
        if (service) {
          if ((service as any).error === 'conflict') {
            await alertDialogue('Intenso', i18n.t('components.connection-intenso.error-template-filter-conflict') as string);
          } else {
            settings.connection = service;          
            context.emit('updated', settings.connection);
          }
        } else {
          await generalError(false);
        }
      }
      settings.loading = false;
    };

    const disable = async () => {
      if ((!props.allowDelete) || settings.loading) return;
      settings.loading = true;
      const confirmed = await confirmDialogue('Intenso', i18n.t('components.connection-intenso.disable-intenso-service'), ModalIconType.warning);
      if (confirmed) {
        if (await disableIntenso(settings.connection.id)) {
          context.emit('updated', null);
        } else {
          await generalError(false);
        }
      }
      settings.loading = false;
    };

    return { settings, enable, disable, ConnectionStatus };
  }
});
